/**
 * A&K Land & Cattle — shared motion layer.
 *
 * Independent pieces, deliberately dependency-free (no @theme imports) so a
 * Horizon upgrade can't break the storytelling sections:
 *
 *   1. Scroll reveal — anything marked [data-ak-reveal] fades/rises into place
 *      once. The hidden state lives in ak-theme.css behind `html.ak-motion`,
 *      a class set inline in layout/theme.liquid, so content is never stranded
 *      invisible if this file fails to load.
 *
 *   2. Count-up — [data-ak-count] figures tick from zero to the number already
 *      in the markup when they scroll into view. The final value is rendered
 *      server-side, so a failure here leaves the correct number on screen.
 *
 *   3. <ak-process> — the "How we work" step list. The step nearest the middle
 *      of the viewport becomes active; the sticky media column crossfades to
 *      that step's photo and the rail fills to match.
 *
 *   4. <ak-timeline> — the About page milestone rail. Milestones latch as they
 *      cross the middle of the viewport and the rail fills to the last one
 *      reached.
 *
 * All of it no-ops under prefers-reduced-motion: elements are simply shown,
 * figures keep their final value, and both scroll-driven sections fall back to
 * a static first frame.
 */

// Tells the failsafe in layout/theme.liquid that the motion layer is alive, so
// it leaves the `ak-motion` class (and therefore the hidden state) in place.
window.akMotionReady = true;

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ------------------------------------------------------------------ reveal */

const revealObserver =
  'IntersectionObserver' in window && !prefersReducedMotion.matches
    ? new IntersectionObserver(
        (entries, observer) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        },
        // Trigger a little before the element reaches the bottom edge so the
        // motion reads as "already happening" rather than "waiting for me".
        { rootMargin: '0px 0px -10% 0px', threshold: 0.01 }
      )
    : null;

/**
 * @param {ParentNode} [root] - Scope to scan. Defaults to the whole document.
 */
function initReveal(root = document) {
  const targets = root.querySelectorAll('[data-ak-reveal]:not(.is-revealed)');

  for (const target of targets) {
    if (revealObserver) {
      revealObserver.observe(target);
    } else {
      target.classList.add('is-revealed');
    }
  }
}

initReveal();

/* ---------------------------------------------------------------- count-up */

/**
 * Figures tick up from zero when they scroll into view.
 *
 * The value is read back out of the element's own text, so the markup carries
 * the real number and this only ever replaces it temporarily. Anything without
 * a recognisable number in it ("Locally Owned") is left alone rather than
 * animated to nothing.
 */

const COUNT_DURATION = 1100;

// Leading text, the figure itself, trailing text — "17,000+" and "$1.2M" and
// "5 Generations" all split cleanly. `s` so a value that wrapped in the editor
// still matches.
const COUNT_PARTS = /^(\D*?)(\d[\d,]*(?:\.\d+)?)(.*)$/s;

/** @param {HTMLElement} element */
function runCount(element) {
  if (element.dataset.akCounted != null) return;

  const parts = element.textContent?.match(COUNT_PARTS);
  if (!parts) return;

  const [, prefix, figure, suffix] = parts;
  const target = Number(figure.replace(/,/g, ''));
  if (!Number.isFinite(target)) return;

  element.dataset.akCounted = '';

  // Match the source formatting rather than imposing our own: a value written
  // "17,000" keeps its separator, "1.5" keeps its one decimal place.
  const decimals = figure.includes('.') ? figure.split('.')[1].length : 0;
  const format = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: figure.includes(','),
  });

  const start = performance.now();

  /** @param {number} now */
  const step = (now) => {
    const progress = Math.min((now - start) / COUNT_DURATION, 1);
    // Ease out cubic — fast off the mark, settles onto the final figure.
    const eased = 1 - (1 - progress) ** 3;
    element.textContent = `${prefix}${format.format(target * eased)}${suffix}`;
    if (progress < 1) window.requestAnimationFrame(step);
  };

  window.requestAnimationFrame(step);
}

const countObserver =
  'IntersectionObserver' in window && !prefersReducedMotion.matches
    ? new IntersectionObserver(
        (entries, observer) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            observer.unobserve(entry.target);
            runCount(/** @type {HTMLElement} */ (entry.target));
          }
        },
        { rootMargin: '0px 0px -15% 0px', threshold: 0.2 }
      )
    : null;

/**
 * @param {ParentNode} [root] - Scope to scan. Defaults to the whole document.
 */
function initCount(root = document) {
  if (!countObserver) return;

  for (const target of root.querySelectorAll('[data-ak-count]:not([data-ak-counted])')) {
    countObserver.observe(target);
  }
}

initCount();

// The theme editor swaps section markup in place, so freshly rendered nodes
// need re-observing or they stay at opacity 0 for the merchant.
document.addEventListener('shopify:section:load', (event) => {
  if (!(event.target instanceof Element)) return;
  initReveal(event.target);
  initCount(event.target);
});

/* ----------------------------------------------------------------- process */

class AkProcess extends HTMLElement {
  /** @type {HTMLElement[]} */
  #steps = [];
  /** @type {HTMLElement[]} */
  #panels = [];
  #active = -1;
  #ticking = false;
  #inView = false;
  /** @type {IntersectionObserver | undefined} */
  #visibility;
  /** @type {MediaQueryList | undefined} */
  #twoColumn;

  connectedCallback() {
    this.#steps = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-ak-step]')]);
    this.#panels = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-ak-panel]')]);

    if (this.#steps.length === 0) return;

    this.#setActive(0);

    if (prefersReducedMotion.matches) return;

    // On a real pointer, hovering a step should preview it immediately —
    // scrolling will take the wheel back on the next frame.
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      this.#steps.forEach((step, index) => {
        step.addEventListener('pointerenter', () => this.#setActive(index));
      });
    }

    // Below 990px the sticky photo column is display:none and every step shows
    // its own image, so there is nothing for scroll position to drive. Don't
    // run a scroll handler on phones to animate something that isn't rendered.
    this.#twoColumn = window.matchMedia('(min-width: 990px)');
    this.#twoColumn.addEventListener('change', this.#onLayoutChange);
    if (this.#twoColumn.matches) this.#track();
  }

  disconnectedCallback() {
    this.#twoColumn?.removeEventListener('change', this.#onLayoutChange);
    this.#untrack();
  }

  /** @param {MediaQueryListEvent} event */
  #onLayoutChange = (event) => {
    if (event.matches) {
      this.#track();
    } else {
      this.#untrack();
      this.#setActive(0);
    }
  };

  #track() {
    // Bind first, observe second. The observer is only an optimisation (stop
    // listening while the section is off screen) — activation must not depend
    // on it having fired, or a missed callback leaves the section frozen on
    // step one.
    this.#bindScroll();
    this.#syncToScroll();

    if ('IntersectionObserver' in window && !this.#visibility) {
      this.#visibility = new IntersectionObserver(this.#onVisibilityChange, { threshold: 0 });
      this.#visibility.observe(this);
    }
  }

  #untrack() {
    this.#visibility?.disconnect();
    this.#visibility = undefined;
    this.#unbindScroll();
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onVisibilityChange = (entries) => {
    const entry = entries[0];
    if (!entry) return;

    if (entry.isIntersecting) {
      this.#bindScroll();
      this.#syncToScroll();
    } else {
      this.#unbindScroll();
    }
  };

  #bindScroll() {
    if (this.#inView) return;
    this.#inView = true;
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });
  }

  #unbindScroll() {
    if (!this.#inView) return;
    this.#inView = false;
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
  }

  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;
    window.requestAnimationFrame(() => {
      this.#ticking = false;
      this.#syncToScroll();
    });
  };

  /**
   * The active step is the last one whose top edge has crossed the middle of
   * the viewport — the same rule the eye uses when reading down a list.
   */
  #syncToScroll() {
    const midpoint = window.innerHeight / 2;
    let next = 0;

    this.#steps.forEach((step, index) => {
      if (step.getBoundingClientRect().top <= midpoint) next = index;
    });

    this.#setActive(next);
  }

  /** @param {number} index */
  #setActive(index) {
    if (index === this.#active) return;
    this.#active = index;

    this.#steps.forEach((step, i) => {
      step.toggleAttribute('data-active', i === index);
    });

    this.#panels.forEach((panel, i) => {
      panel.toggleAttribute('data-active', i === index);
    });

    this.style.setProperty('--ak-process-progress', `${(index + 1) / this.#steps.length}`);
  }
}

if (!customElements.get('ak-process')) {
  customElements.define('ak-process', AkProcess);
}

/* ---------------------------------------------------------------- timeline */

/**
 * The About page milestone rail.
 *
 * Each milestone latches to `data-passed` once its dot crosses the middle of
 * the viewport, and the rail's coloured fill is drawn down to the last dot
 * reached. Latching (rather than tracking a single active step) is deliberate:
 * a history reads as accumulating, so scrolling back up should leave the rail
 * filled behind you.
 *
 * Without JS, or under reduced motion, every milestone is legible and the fill
 * simply stays at zero — see the fallback notes in sections/ak-timeline.liquid.
 */
class AkTimeline extends HTMLElement {
  /** @type {HTMLElement[]} */
  #milestones = [];
  #reached = -1;
  #fill = 0;
  #ticking = false;
  #inView = false;
  /** @type {IntersectionObserver | undefined} */
  #visibility;

  connectedCallback() {
    this.#milestones = /** @type {HTMLElement[]} */ ([
      ...this.querySelectorAll('[data-ak-milestone]'),
    ]);

    if (this.#milestones.length === 0 || prefersReducedMotion.matches) return;

    // Bind first, observe second — same reasoning as <ak-process>: the observer
    // is only there to stop listening off screen, so activation must not
    // depend on it having fired.
    this.#bindScroll();
    this.#sync();

    if ('IntersectionObserver' in window) {
      this.#visibility = new IntersectionObserver(this.#onVisibilityChange, { threshold: 0 });
      this.#visibility.observe(this);
    }
  }

  disconnectedCallback() {
    this.#visibility?.disconnect();
    this.#visibility = undefined;
    this.#unbindScroll();
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onVisibilityChange = (entries) => {
    const entry = entries[0];
    if (!entry) return;

    if (entry.isIntersecting) {
      this.#bindScroll();
      this.#sync();
    } else {
      this.#unbindScroll();
    }
  };

  #bindScroll() {
    if (this.#inView) return;
    this.#inView = true;
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });
  }

  #unbindScroll() {
    if (!this.#inView) return;
    this.#inView = false;
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
  }

  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;
    window.requestAnimationFrame(() => {
      this.#ticking = false;
      this.#sync();
    });
  };

  #sync() {
    const midpoint = window.innerHeight / 2;
    const trackTop = this.getBoundingClientRect().top;
    let reached = -1;
    let fill = 0;

    this.#milestones.forEach((milestone, index) => {
      const dot = milestone.querySelector('[data-ak-dot]') ?? milestone;
      const box = dot.getBoundingClientRect();
      const center = box.top + box.height / 2;

      if (center > midpoint) return;

      reached = index;
      fill = center - trackTop;
    });

    // Once the last dot is behind us, run the fill to the end of the track
    // rather than stopping short at that dot.
    if (reached === this.#milestones.length - 1) fill = this.offsetHeight;

    // Latch, in both directions at once: a dot that has been passed stays
    // passed AND the fill only ever grows. Letting the fill follow live scroll
    // position would recede back past dots that are still lit, which reads as
    // a bug rather than as scrubbing.
    if (reached > this.#reached) {
      this.#reached = reached;

      this.#milestones.forEach((milestone, index) => {
        if (index <= reached) milestone.setAttribute('data-passed', '');
      });
    }

    if (fill > this.#fill) {
      this.#fill = fill;
      this.style.setProperty('--ak-timeline-fill', `${fill}px`);
    }
  }
}

if (!customElements.get('ak-timeline')) {
  customElements.define('ak-timeline', AkTimeline);
}
